import { ChevronsRight } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useTheme } from "../theme-provider";

import RenderSidebarRoutes from "./RenderSideBar";
import SidebarFooter from "./SidebarFooter";
import WorkspaceAvatarWithXero from "./components/WorkspaceAvatarWithXero";

import { Accordion } from "@/components/ui/accordion";
import { cn, isAdminDomain } from "@/lib/utils";

import aldLogo from "/ald-logo.svg";
import logo from "/fav.png";

import useMessageCounter from "@/pages/messages/hooks/useMessageCounter";
import { hoverExpanded, toggleExpanded } from "@/redux/slices/configSlice";
import { RootState } from "@/redux/store";

const Sidebar = (): React.JSX.Element => {
  const dispatch = useDispatch();
  const expanded = useSelector((state: RootState) => state.config.expanded);
  const isHover = useSelector((state: RootState) => state.config.hover);
  const workspace = useSelector(
    (state: RootState) => state.user?.workspaceData,
  );
  const skin = useTheme();
  const [showUser, _setShowUser] = useState(true);

  const [isInteracting, setInteracting] = useState(false);

  const toggleHover = React.useCallback(
    (config: boolean): void => {
      dispatch(hoverExpanded(config));
    },
    [dispatch],
  );

  // for message count
  const { updatedBusinessRoutes } = useMessageCounter();

  // Effect to handle expanded and interaction states
  useEffect(() => {
    if (!expanded && !isInteracting) {
      toggleHover(false);
    }
  }, [expanded, isInteracting, toggleHover]);

  return (
    <aside
      className={cn(
        `h-screen hidden lg:fixed lg:block ${skin?.theme === "dark" ? "bg-[#020817]" : "bg-white"} z-50 max-w-[245px] top-0 left-0 duration-300 ease-in-out ${expanded || isHover ? "w-[245px]" : "w-20"}`,
      )}
    >
      <nav
        className='flex flex-col h-full duration-300 ease-in-out border-r shadow-sm'
        onMouseEnter={() => toggleHover(true)}
        onMouseLeave={() => !isInteracting && toggleHover(false)}
      >
        <div className='flex items-center justify-between w-full min-w-0 px-4 py-3'>
          {expanded || isHover ? (
            <div className='flex items-center justify-between w-full min-w-0 gap-2 '>
              <div className='flex items-center min-w-0 gap-2'>
                {isAdminDomain ? (
                  <img
                    src={workspace?.logo ?? aldLogo}
                    className='flex-shrink-0 object-contain h-8 rounded-md select-none drag-none'
                    alt='logo'
                  />
                ) : (
                  <WorkspaceAvatarWithXero />
                )}
              </div>
            </div>
          ) : (
            <div className='flex-shrink-0'>
              {isAdminDomain ? (
                <img src={logo} className='rounded-md size-10' alt='logo' />
              ) : (
                <img
                  src={workspace?.logo || logo}
                  className='rounded-md size-10'
                  alt='logo'
                />
              )}
            </div>
          )}
          {(expanded || isHover) && (
            <ChevronsRight
              strokeWidth={1.7}
              color='#214090'
              size={27}
              onClick={() => dispatch(toggleExpanded())}
              className={cn(
                "cursor-pointer flex-shrink-0 transition-transform origin-center will-change-transform duration-300 ease-in-out place-self-start",
                expanded ? "rotate-180" : "rotate-0",
              )}
            />
          )}
        </div>

        <div className='flex-1 pb-2 overflow-y-scroll scrollbar-none sidebar_scrollbar'>
          <ul className='px-2'>
            <Accordion type='single' collapsible className='space-y-1.5'>
              {RenderSidebarRoutes({ updatedBusinessRoutes })}
            </Accordion>
          </ul>
        </div>
        {(expanded || isHover) && showUser && (
          <SidebarFooter
            isInteracting={isInteracting}
            setInteracting={setInteracting}
          />
        )}
      </nav>
    </aside>
  );
};

export default Sidebar;
